import React, { useState, useEffect, useMemo } from "react";
import {
  DataField,
  ToggleField,
  OwnerDropdownField,
  formatCurrency,
  formatAccountType,
} from "../shared/DataField";
import EditControls from "../shared/EditControls";
import ShareSlider, { SharePresetButtons } from "../../ShareSlider";
import { formatAccountName, formatCurrencyPreservingScale, formatDateForDisplay, formatDateForInput, isTwoOwnerAccount, isSpecialRegistration, isRetirementAccount } from "../../../utils/formatters";
import {
  isBeneficiaryShareTotalComplete,
  isBeneficiaryShareTotalOver100,
} from "../../../api/updateHouseholdAPI";
import { isRevocableTrust, getTrusteeMemberId, getTrustGrantorIds } from "../../../utils/trustInfoHelpers";
import {
  getHtmlDateMinMax,
  normalizeToYmd,
  validateScheduleFutureYmd,
} from "../../../utils/dateFieldConstraints";
import {
  DEPOSIT_METHOD_OPTIONS,
  PROCEEDS_OPTIONS,
  SECURITY_PURCHASES_OPTIONS,
  DIVIDEND_OPTIONS,
  TYPE_OF_TRANSFER_OPTIONS,
  FREQUENCY_OPTIONS,
  DESTINATION_TYPE_OPTIONS,
  normalizeAccountOptionValue,
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_NEEDS_MAP,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  LIQUIDITY_TIME_HORIZON_MAP,
  getLiquidityNeedsLabel,
  getLiquidityTimeHorizonLabel,
  REGISTRATION_OPTIONS,
  getAccountTypesForRegistration,
  INHERITED_ACCOUNT_TYPES,
  ESTATE_ACCOUNT_TYPES,
  TRUST_TAX_ID_TYPE_OPTIONS,
  getTaxClassificationOptions,
} from "../../../constants/accountConstants";
import { deliveringFirmOptions } from "../../../constants/deliveringFirms";
import { RELATIONSHIP_OPTIONS } from "../../../constants/membersConstants";
import StateCitySelect from "../../StateCitySelect";
import MemberSelect from "../../shared/MemberSelect";
import { US_STATES } from "../../../data/usStatesAndCities";
import { resolveStateName } from "../../../utils/stateUtils";
import {
  getBrokerageFlags,
  showSourceOfFunds,
  showStatutoryTrust,
  showUseOfFunds,
  showLiquidityRequirements,
} from "../../../utils/brokerageFields";
import {
  PRIMARY_OBJECTIVE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  DECISION_MAKING_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  PRIMARY_USE_OPTIONS,
  ACCOUNT_TIME_HORIZON_OPTIONS,
  ACCOUNT_TIME_HORIZON_MAP,
  getAccountRiskTimeHorizonLabel,
} from "../../../constants/householdConstants";
import BankAccountAccordion from "../../BankAccountAccordion";
import {
  emptyBankOps,
  formatBankLabel,
  getAccountBanks,
  resolveVisibleBanks,
} from "../../../utils/bankAccounts";

const TIME_HORIZON_OPTIONS = ACCOUNT_TIME_HORIZON_OPTIONS;
const TIME_HORIZON_MAP = ACCOUNT_TIME_HORIZON_MAP;
const USE_OF_FUNDS_OPTIONS = PRIMARY_USE_OPTIONS;

const timeHorizonToDisplay = (th) => getAccountRiskTimeHorizonLabel(th, "N/A");

const liquidityNeedsToDisplay = (liquidityReq) => {
  return getLiquidityNeedsLabel(liquidityReq?.amount, "N/A");
};

const liquidityTimeHorizonToDisplay = (liquidityReq) => {
  return getLiquidityTimeHorizonLabel(liquidityReq?.time_horizon, "N/A");
};

const ReviewAccountsTab = ({
  householdData,
  isEditing,
  formData,
  onChange,
  onEdit,
  onSave,
  onCancel,
  saving,
  validationErrors,
  onFieldError,
  onAddBeneficiary,
  onAddTrustedContact,
  onRemoveBeneficiary,
  onRemoveTrustedContact,
  removedBeneficiaries,
  removedTrustedContacts,
  onAddContribution,
  onAddWithdrawal,
  onDeleteDeposit,
  onDeleteWithdrawal,
  onLinkBankAccount,
  onRemoveBankAccount,
}) => {
  const accounts = useMemo(
    () => (Array.isArray(householdData?.accounts) ? householdData.accounts : []),
    [householdData?.accounts],
  );
  const members = householdData?.members || [];
  const brokerageFlags = getBrokerageFlags(householdData || {});
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedSection, setSelectedSection] = useState("basic_details");

  useEffect(() => {
    if (accounts.length > 0 && (!selectedAccountId || !accounts.find((a) => a.id === selectedAccountId))) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  useEffect(() => {
    setSelectedSection("basic_details");
  }, [selectedAccountId]);

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  useEffect(() => {
    if (!selectedAccount) return;
    if (selectedSection === "trust_info" && selectedAccount.registration?.toLowerCase() !== "trust") {
      setSelectedSection("basic_details");
    }
    if (selectedSection === "business_info" && selectedAccount.registration?.toLowerCase() !== "business") {
      setSelectedSection("basic_details");
    }
    if (selectedSection === "other_info" && !isSpecialRegistration(selectedAccount.registration)) {
      setSelectedSection("basic_details");
    }
    if (selectedSection === "inheritance_info" && !INHERITED_ACCOUNT_TYPES.includes(selectedAccount.type)) {
      setSelectedSection("basic_details");
    }
    if (selectedSection === "estate_info" && !ESTATE_ACCOUNT_TYPES.includes(selectedAccount.type)) {
      setSelectedSection("basic_details");
    }
    if (isSpecialRegistration(selectedAccount.registration) && 
        ["contributions", "withdrawals", "bank_account_details", "transfer_details", "beneficiaries", "trusted_contacts"].includes(selectedSection)) {
      setSelectedSection("basic_details");
    }
  }, [selectedAccount, selectedSection]);

  const getFormValue = (path, fallback) => {
    if (!formData || !selectedAccountId) return fallback;
    const accountData = formData[selectedAccountId];
    if (!accountData) return fallback;
    const keys = path.split(".");
    let current = accountData;
    for (const key of keys) {
      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        return fallback;
      }
    }
    return current !== undefined ? current : fallback;
  };

  const handleFieldChange = (fieldPath, value) => {
    if (onChange && selectedAccountId) {
      onChange(selectedAccountId, fieldPath, value);
    }
  };

  const handleTimeHorizonChange = (_fieldPath, displayValue) => {
    const range = TIME_HORIZON_MAP[displayValue];
    if (range && onChange && selectedAccountId) {
      onChange(selectedAccountId, "risk_tolerance.time_horizon.start", range.start);
      onChange(selectedAccountId, "risk_tolerance.time_horizon.end", range.end);
    }
  };

  const getTimeHorizonFormValue = (_path, fallback) => {
    const storedStart = getFormValue("risk_tolerance.time_horizon.start", null);
    const storedEnd = getFormValue("risk_tolerance.time_horizon.end", null);
    if (storedStart !== null && storedEnd !== null) {
      const entry = Object.entries(TIME_HORIZON_MAP).find(
        ([, v]) => Number(v.start) === Number(storedStart) && Number(v.end) === Number(storedEnd),
      );
      if (entry) return entry[0];
      const label = timeHorizonToDisplay({ start: storedStart, end: storedEnd });
      return label !== "N/A" ? label : fallback;
    }
    return fallback;
  };

  const handleLiquidityNeedsChange = (_fieldPath, displayValue) => {
    const range = LIQUIDITY_NEEDS_MAP[displayValue];
    if (range && onChange && selectedAccountId) {
      onChange(selectedAccountId, "risk_tolerance.liquidity_requirements.amount.start", range.start);
      onChange(selectedAccountId, "risk_tolerance.liquidity_requirements.amount.end", range.end);
    }
  };

  const getLiquidityNeedsFormValue = (_path, fallback) => {
    const storedStart = getFormValue("risk_tolerance.liquidity_requirements.amount.start", null);
    const storedEnd = getFormValue("risk_tolerance.liquidity_requirements.amount.end", null);
    if (storedStart !== null && storedEnd !== null) {
      return getLiquidityNeedsLabel({ start: storedStart, end: storedEnd }, fallback);
    }
    return fallback;
  };

  const handleLiquidityTimeHorizonChange = (_fieldPath, displayValue) => {
    const range = LIQUIDITY_TIME_HORIZON_MAP[displayValue];
    if (range && onChange && selectedAccountId) {
      onChange(selectedAccountId, "risk_tolerance.liquidity_requirements.time_horizon.start", range.start);
      onChange(selectedAccountId, "risk_tolerance.liquidity_requirements.time_horizon.end", range.end);
    }
  };

  const getLiquidityTimeHorizonFormValue = (_path, fallback) => {
    const storedStart = getFormValue("risk_tolerance.liquidity_requirements.time_horizon.start", null);
    const storedEnd = getFormValue("risk_tolerance.liquidity_requirements.time_horizon.end", null);
    if (storedStart !== null && storedEnd !== null) {
      return getLiquidityTimeHorizonLabel(
        { start: storedStart, end: storedEnd },
        fallback,
      );
    }
    return fallback;
  };

  const getMemberNameById = (memberId) => {
    if (!memberId) return "N/A";
    const member = members.find((m) => m.id === memberId);
    if (!member) return "N/A";
    return `${member.personal_info?.first_name || ""} ${member.personal_info?.last_name || ""}`.trim() || "N/A";
  };

  /** Grantor field may be stored as member id or as display name from older edits. */
  const resolveMemberIdFromFormValue = (raw) => {
    if (raw == null || raw === "") return "";
    const s = String(raw).trim();
    if (!s) return "";
    if (members.some((m) => String(m.id) === s)) return s;
    const byName = members.find(
      (m) =>
        `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim() === s,
    );
    return byName ? String(byName.id) : s;
  };

  const getMemberSsnDigitsById = (memberId) => {
    if (!memberId) return "";
    const member = members.find((m) => m.id === memberId);
    const raw = member?.personal_info?.ssn_or_tin;
    return String(raw || "").replace(/\D/g, "");
  };

  if (accounts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No accounts found.</p>
      </div>
    );
  }

  const riskTol = selectedAccount?.risk_tolerance || {};
  const scheduleBounds = getHtmlDateMinMax("schedule_future");

  const accountScopedFieldError = (path, err) => {
    if (onFieldError && selectedAccountId) {
      onFieldError(`${selectedAccountId}.${path}`, err);
    }
  };

  const trustRevocableCurrent =
    isRevocableTrust(selectedAccount?.type);
  const trustGrantorIds = getFormValue(
    "trust_info.grantorIds",
    getTrustGrantorIds(selectedAccount?.trust_info),
  );
  const trustPrimaryGrantorId =
    (Array.isArray(trustGrantorIds) ? trustGrantorIds : []).find(Boolean) || "";
  const trustGrantorSsnDigits = getMemberSsnDigitsById(trustPrimaryGrantorId);
  const trustTaxIdValue = trustRevocableCurrent
    ? trustGrantorSsnDigits
    : selectedAccount?.trust_info?.ssn_or_tin || "N/A";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Accounts</h2>
        <EditControls
          isEditing={isEditing}
          onEdit={onEdit}
          onSave={onSave}
          onCancel={onCancel}
          saving={saving}
          disabled={Object.keys(validationErrors || {}).length > 0}
        />
      </div>

      {/* Account selector */}
      <div className="flex flex-nowrap gap-2 mb-6 overflow-x-auto pt-1 pb-2 -mx-1 px-1">
        {accounts.map((account, index) => (
          <button
            key={account.id}
            onClick={() => setSelectedAccountId(account.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              selectedAccountId === account.id
                ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {formatAccountName(account, index + 1, members)}
          </button>
        ))}
      </div>

      {/* Account detail sections */}
      {selectedAccount && (
        <div>
          <div className="overflow-x-auto -mx-1 px-1 pb-3 mb-4 border-b border-gray-200">
            <div className="flex flex-nowrap sm:flex-wrap items-center gap-x-4 gap-y-2 min-w-min sm:min-w-0">
              <div className="flex items-center gap-2 flex-nowrap sm:flex-wrap rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-100 shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mr-1 whitespace-nowrap">
                  Account Info
                </span>
                {[
                  { key: "basic_details", label: "Basic Details" },
                  ...(!isSpecialRegistration(selectedAccount.registration)
                    ? [{ key: "transfer_details", label: "Transfer Details" }]
                    : []),
                  ...(selectedAccount.registration?.toLowerCase() === "trust"
                    ? [{ key: "trust_info", label: "Trust Info" }]
                    : []),
                  ...(selectedAccount.registration?.toLowerCase() === "business"
                    ? [{ key: "business_info", label: "Business Info" }]
                    : []),
                  ...(INHERITED_ACCOUNT_TYPES.includes(selectedAccount.type)
                    ? [{ key: "inheritance_info", label: "Inheritance Info" }]
                    : []),
                  ...(ESTATE_ACCOUNT_TYPES.includes(selectedAccount.type)
                    ? [{ key: "estate_info", label: "Estate Info" }]
                    : []),
                  ...(isSpecialRegistration(selectedAccount.registration)
                    ? [{ key: "other_info", label: "Other Info" }]
                    : []),
                  ...(!isSpecialRegistration(selectedAccount.registration)
                    ? [
                        { key: "beneficiaries", label: "Beneficiaries" },
                        { key: "trusted_contacts", label: "Trusted Contacts" },
                      ]
                    : []),
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSelectedSection(s.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                      selectedSection === s.key
                        ? "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300"
                        : "text-gray-700 hover:bg-white/60"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {!isSpecialRegistration(selectedAccount.registration) && (
                <div className="flex items-center gap-2 flex-nowrap sm:flex-wrap rounded-lg px-3 py-2 bg-green-50 border border-green-100 shrink-0">
                  <span className="text-xs font-semibold uppercase tracking-wide text-green-700 mr-1 whitespace-nowrap">
                    Money Movements
                  </span>
                  {[
                    { key: "contributions", label: "Contributions" },
                    { key: "withdrawals", label: "Withdrawals" },
                    { key: "bank_account_details", label: "Linked Bank Account" }
                  ].map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setSelectedSection(s.key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                        selectedSection === s.key
                          ? "bg-green-200 text-green-900 ring-1 ring-green-300"
                          : "text-gray-700 hover:bg-white/60"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            {/* ═══════════════ BASIC DETAILS ═══════════════ */}
            {selectedSection === "basic_details" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                  {(() => {
                    const ownerId1 = getFormValue("owners_info.0.id", selectedAccount.owners_info?.[0]?.id);
                    const ownerId2 = getFormValue("owners_info.1.id", selectedAccount.owners_info?.[1]?.id);
                    const ownerName1 = ownerId1 ? getMemberNameById(ownerId1) : "";
                    const ownerName2 = ownerId2 ? getMemberNameById(ownerId2) : "";
                    return (
                      <>
                        <OwnerDropdownField
                          label="Owner 1"
                          value={ownerName1}
                          isEditable
                          isEditing={isEditing}
                          excludeValue={ownerName2 || null}
                          householdMembers={members}
                          fieldPath="owners_info.0.id"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                        <OwnerDropdownField
                          label="Owner 2"
                          value={ownerName2}
                          isEditable
                          isEditing={isEditing}
                          disabled={!isTwoOwnerAccount(selectedAccount)}
                          excludeValue={ownerName1 || null}
                          householdMembers={members}
                          fieldPath="owners_info.1.id"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                      </>
                    );
                  })()}
                  <DataField
                    label="New Account Number"
                    value={selectedAccount.new_account_number || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    fieldPath="new_account_number"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                  />
                  <DataField
                    label="Custodian"
                    value={selectedAccount.custodian || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    fieldPath="custodian"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                  />
                  <DataField
                    label="Account Registration"
                    value={selectedAccount.registration || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    fieldPath="registration"
                    onFieldChange={(path, val) => {
                      handleFieldChange(path, val);
                      handleFieldChange("type", "");
                    }}
                    getFormValue={getFormValue}
                    options={REGISTRATION_OPTIONS}
                  />
                  <DataField
                    label="Type"
                    value={selectedAccount.type || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    fieldPath="type"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                    options={getAccountTypesForRegistration(getFormValue?.("registration", selectedAccount.registration) || selectedAccount.registration)}
                  />

                  <DataField
                    label="Account Value"
                    value={formatCurrency(selectedAccount.account_value)}
                    isEditable
                    isEditing={isEditing}
                    type="number"
                    min={0}
                    max={50000000}
                    fieldPath="account_value"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                    disableWheel
                    error={validationErrors?.[`${selectedAccountId}.account_value`]}
                    onFieldError={onFieldError}
                  />
                  <DataField
                    label="Proceeds"
                    value={selectedAccount.proceeds || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    options={PROCEEDS_OPTIONS}
                    fieldPath="proceeds"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                  />
                  <DataField
                    label="Security Purchases"
                    value={selectedAccount.security_purchases || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    options={SECURITY_PURCHASES_OPTIONS}
                    fieldPath="security_purchases"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                  />
                  <DataField
                    label="Dividend/Distribution Income"
                    value={selectedAccount.distribution_income || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    options={DIVIDEND_OPTIONS}
                    fieldPath="distribution_income"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 mt-4">
                  {!isRetirementAccount(getFormValue?.("registration", selectedAccount.registration) || selectedAccount.registration) && (
                    <ToggleField
                      label="Are Margins enabled?"
                      value={selectedAccount.margin ?? false}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="margin"
                      onFieldChange={handleFieldChange}
                      formData={formData?.[selectedAccountId]}
                    />
                  )}
                  <ToggleField
                    label="Are Options enabled?"
                    value={selectedAccount.options ?? false}
                    isEditable
                    isEditing={isEditing}
                    fieldPath="options"
                    onFieldChange={handleFieldChange}
                    formData={formData?.[selectedAccountId]}
                  />

                  <ToggleField
                    label="Is Check Writing enabled?"
                    value={selectedAccount.check_writing ?? false}
                    isEditable
                    isEditing={isEditing}
                    fieldPath="check_writing"
                    onFieldChange={handleFieldChange}
                    formData={formData?.[selectedAccountId]}
                  />
                </div>
              </>
            )}

            {/* ═══════════════ INVESTMENT STRATEGY (hidden from client portal) ═══════════════ */}
            {false && selectedSection === "investment_strategy" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                <DataField
                  label="Primary Investment Objective"
                  value={riskTol.invest_objectives?.[0] || "N/A"}
                  isEditable
                  isEditing={isEditing}
                  options={PRIMARY_OBJECTIVE_OPTIONS}
                  fieldPath="risk_tolerance.invest_objectives.0"
                  onFieldChange={handleFieldChange}
                  getFormValue={getFormValue}
                />
                <DataField
                  label="Risk Tolerance"
                  value={riskTol.risk_tolerance || "N/A"}
                  isEditable
                  isEditing={isEditing}
                  options={RISK_TOLERANCE_OPTIONS}
                  fieldPath="risk_tolerance.risk_tolerance"
                  onFieldChange={handleFieldChange}
                  getFormValue={getFormValue}
                />
                <DataField
                  label="Time Horizon Range (years)"
                  value={timeHorizonToDisplay(riskTol.time_horizon)}
                  isEditable
                  isEditing={isEditing}
                  options={TIME_HORIZON_OPTIONS}
                  fieldPath="risk_tolerance.time_horizon_display"
                  onFieldChange={handleTimeHorizonChange}
                  getFormValue={getTimeHorizonFormValue}
                />
                <DataField
                  label="Account Decision Making"
                  value={riskTol.decision_making || "N/A"}
                  isEditable
                  isEditing={isEditing}
                  options={DECISION_MAKING_OPTIONS}
                  fieldPath="risk_tolerance.decision_making"
                  onFieldChange={handleFieldChange}
                  getFormValue={getFormValue}
                />
                {showSourceOfFunds(brokerageFlags) && (
                  <DataField
                    label="Source of Funds"
                    value={
                      Array.isArray(riskTol.source_of_funds)
                        ? riskTol.source_of_funds.join(", ")
                        : riskTol.source_of_funds || "N/A"
                    }
                    isEditable
                    isEditing={isEditing}
                    options={SOURCE_OF_FUNDS_OPTIONS}
                    fieldPath="risk_tolerance.source_of_funds"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                  />
                )}
                {showUseOfFunds(brokerageFlags) && (
                  <DataField
                    label="Primary Use of Funds"
                    value={riskTol.use_of_funds || "N/A"}
                    isEditable
                    isEditing={isEditing}
                    options={PRIMARY_USE_OPTIONS}
                    fieldPath="risk_tolerance.use_of_funds"
                    onFieldChange={handleFieldChange}
                    getFormValue={getFormValue}
                  />
                )}
                {showLiquidityRequirements(brokerageFlags) && (
                  <DataField
                    label="Liquidity Needs ($)"
                    value={liquidityNeedsToDisplay(riskTol.liquidity_requirements)}
                    isEditable
                    isEditing={isEditing}
                    options={LIQUIDITY_NEEDS_OPTIONS}
                    fieldPath="risk_tolerance.liquidity_requirements.amount_display"
                    onFieldChange={handleLiquidityNeedsChange}
                    getFormValue={getLiquidityNeedsFormValue}
                  />
                )}
                {showLiquidityRequirements(brokerageFlags) && (
                  <DataField
                    label="Liquidity Time Horizon (years)"
                    value={liquidityTimeHorizonToDisplay(riskTol.liquidity_requirements)}
                    isEditable
                    isEditing={isEditing}
                    options={LIQUIDITY_TIME_HORIZON_OPTIONS}
                    fieldPath="risk_tolerance.liquidity_requirements.time_horizon_display"
                    onFieldChange={handleLiquidityTimeHorizonChange}
                    getFormValue={getLiquidityTimeHorizonFormValue}
                  />
                )}
              </div>
            )}

            {/* ═══════════════ TRANSFER DETAILS ═══════════════ */}
            {selectedSection === "transfer_details" && (
              <>
                {selectedAccount.transfer_info ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                      <DataField
                        label="Existing Account Number"
                        value={selectedAccount.transfer_info?.old_account_number || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="transfer_info.old_account_number"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                      <DataField
                        label="Delivering Firm Name"
                        value={selectedAccount.transfer_info?.delivering_firm_name || "N/A"}
                        options={deliveringFirmOptions(
                          selectedAccount.transfer_info?.delivering_firm_name,
                        )}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="transfer_info.delivering_firm_name"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                      <DataField
                        label="Address Line 1"
                        value={selectedAccount.transfer_info?.address?.address_line_1 || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="transfer_info.address.address_line_1"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                      {isEditing ? (
                        <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                          <StateCitySelect
                            countryValue={getFormValue("transfer_info.address.country", selectedAccount.transfer_info?.address?.country || "USA")}
                            stateValue={getFormValue("transfer_info.address.state", selectedAccount.transfer_info?.address?.state || "")}
                            cityValue={getFormValue("transfer_info.address.city", selectedAccount.transfer_info?.address?.city || "")}
                            onCountryChange={(val) => handleFieldChange("transfer_info.address.country", val)}
                            onStateChange={(val) => {
                              handleFieldChange("transfer_info.address.state", val);
                              if (!getFormValue("transfer_info.address.country", selectedAccount.transfer_info?.address?.country)) {
                                handleFieldChange("transfer_info.address.country", "USA");
                              }
                            }}
                            onCityChange={(val) => {
                              handleFieldChange("transfer_info.address.city", val);
                              if (!getFormValue("transfer_info.address.country", selectedAccount.transfer_info?.address?.country)) {
                                handleFieldChange("transfer_info.address.country", "USA");
                              }
                            }}
                            isEditing={true}
                            stateLabel="State"
                            cityLabel="City"
                          />
                        </div>
                      ) : (
                        <>
                          <DataField label="City" value={selectedAccount.transfer_info?.address?.city || "N/A"} />
                          <DataField label="State" value={resolveStateName(selectedAccount.transfer_info?.address?.state) || "N/A"} />
                          <DataField label="Country" value={selectedAccount.transfer_info?.address?.country || "N/A"} />
                        </>
                      )}
                      <DataField
                        label="ZIP Code"
                        value={selectedAccount.transfer_info?.address?.postal_code || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="transfer_info.address.postal_code"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                        maxLength={5}
                      />
                      <DataField
                        label="Delivery Firm Phone"
                        value={selectedAccount.transfer_info?.address?.phone || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        type="phone"
                        fieldPath="transfer_info.address.phone"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                      <DataField
                        label="Account Registration"
                        value={selectedAccount.transfer_info?.registration_type || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="transfer_info.registration_type"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                      <DataField
                        label="Transfer Type"
                        value={selectedAccount.transfer_info?.transfer_type || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        options={TYPE_OF_TRANSFER_OPTIONS}
                        fieldPath="transfer_info.transfer_type"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                    </div>
                    <div className="mt-4">
                      <ToggleField
                        label="Full Transfer"
                        value={selectedAccount.transfer_info?.is_full_transfer ?? false}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="transfer_info.is_full_transfer"
                        onFieldChange={handleFieldChange}
                        formData={formData?.[selectedAccountId]}
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No transfer details for this account.</p>
                )}
              </>
            )}

            {/* ═══════════════ TRUST INFO ═══════════════ */}
            {selectedSection === "trust_info" &&
              selectedAccount.registration?.toLowerCase() === "trust" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    <DataField
                      label="Trust Name"
                      value={selectedAccount.trust_info?.trust_name || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="trust_info.trust_name"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    <DataField
                      label="Trust Title(as per document)"
                      value={selectedAccount.trust_info?.trust_title || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="trust_info.trust_title"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    <DataField
                      label="Tax ID Number"
                      value={trustTaxIdValue}
                      isEditable={!trustRevocableCurrent}
                      isEditing={isEditing}
                      type="ssn"
                      ssnLengthOnly
                      fieldPath="trust_info.ssn_or_tin"
                      onFieldChange={handleFieldChange}
                      getFormValue={trustRevocableCurrent ? null : getFormValue}
                      error={validationErrors?.[`${selectedAccountId}.trust_info.ssn_or_tin`]}
                      onFieldError={accountScopedFieldError}
                    />
                    <DataField
                      label="Execution Date"
                      value={formatDateForDisplay(selectedAccount.trust_info?.execution_date)}
                      isEditable
                      isEditing={isEditing}
                      type="date"
                      dateValue={selectedAccount.trust_info?.execution_date}
                      fieldPath="trust_info.execution_date"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                      dateConstraint="past_only"
                      error={validationErrors?.[`${selectedAccountId}.trust_info.execution_date`]}
                      onFieldError={accountScopedFieldError}
                    />
                    <DataField
                      label="Amended Date (optional)"
                      value={formatDateForDisplay(selectedAccount.trust_info?.amended_date)}
                      isEditable
                      isEditing={isEditing}
                      type="date"
                      dateValue={selectedAccount.trust_info?.amended_date}
                      fieldPath="trust_info.amended_date"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                      dateConstraint="past_only"
                      error={validationErrors?.[`${selectedAccountId}.trust_info.amended_date`]}
                      onFieldError={accountScopedFieldError}
                    />
                    <DataField
                      label="State"
                      value={resolveStateName(selectedAccount.trust_info?.trust_state) || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="trust_info.trust_state"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                      options={US_STATES.map(s => s.abbreviation)}
                      optionLabels={US_STATES.map(s => `${s.name} (${s.abbreviation})`)}
                    />
                    {showStatutoryTrust(brokerageFlags) && (
                      <ToggleField
                        label="Statutory Trust"
                        value={selectedAccount.trust_info?.statutory_trust ?? false}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="trust_info.statutory_trust"
                        onFieldChange={handleFieldChange}
                        formData={formData?.[selectedAccountId]}
                        variant="field"
                      />
                    )}
                    <DataField
                      label="Tax ID Type"
                      value={selectedAccount.trust_info?.tax_id_type || ""}
                      isEditable
                      isEditing={isEditing}
                      options={TRUST_TAX_ID_TYPE_OPTIONS}
                      fieldPath="trust_info.tax_id_type"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                  </div>
                  {/* Grantors multi-add */}
                  {(() => {
                    const grantorIdsForm = getFormValue(
                      "trust_info.grantorIds",
                      getTrustGrantorIds(selectedAccount.trust_info),
                    );
                    const grantorRows = Array.isArray(grantorIdsForm) && grantorIdsForm.length > 0
                      ? grantorIdsForm
                      : [];
                    const assignedGrantors = new Set(grantorRows.map(String).filter(Boolean));
                    const canAddGrantor = members.some((m) => !assignedGrantors.has(String(m.id)));
                    const setGrantors = (next) => handleFieldChange("trust_info.grantorIds", next);
                    if (!isEditing && grantorRows.length === 0) return null;
                    return (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Grantors / Settlers</h4>
                        {!isEditing ? (
                          <div className="space-y-1">
                            {grantorRows.map((gId, idx) => (
                              <p key={`grantor-ro-${idx}`} className="text-sm text-gray-900">
                                {getMemberNameById(gId)}
                              </p>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {grantorRows.map((gId, idx) => {
                              const takenByOthers = new Set(
                                grantorRows
                                  .filter((_, i) => i !== idx)
                                  .map(String)
                                  .filter(Boolean),
                              );
                              const available = members.filter(
                                (m) =>
                                  !takenByOthers.has(String(m.id)) ||
                                  String(m.id) === String(gId || ""),
                              );
                              return (
                                <div key={`grantor-${idx}`} className="flex gap-2 items-end">
                                  <select
                                    value={gId || ""}
                                    onChange={(e) => {
                                      const next = [...grantorRows];
                                      next[idx] = e.target.value;
                                      setGrantors(next);
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  >
                                    <option value="">Select member...</option>
                                    {available.map((m) => (
                                      <option key={m.id} value={m.id}>
                                        {`${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim() || "Member"}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => setGrantors(grantorRows.filter((_, i) => i !== idx))}
                                    className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                                  >
                                    Remove
                                  </button>
                                </div>
                              );
                            })}
                            {canAddGrantor && (
                              <button
                                type="button"
                                onClick={() => setGrantors([...grantorRows, ""])}
                                className="mt-1 px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold"
                              >
                                + Add Grantor
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    const trusteesBase = Array.isArray(selectedAccount.trust_info?.trustees)
                      ? selectedAccount.trust_info.trustees
                      : [];
                    const tForm = formData?.[selectedAccountId]?.trust_info?.trustees;
                    const formIdxs =
                      tForm && typeof tForm === "object"
                        ? Object.keys(tForm)
                            .map(Number)
                            .filter(Number.isFinite)
                        : [];
                    const maxForm = formIdxs.length ? Math.max(...formIdxs) : -1;
                    const rowCount = isEditing
                      ? Math.max(trusteesBase.length, maxForm + 1, 1)
                      : trusteesBase.length;
                    if (rowCount === 0) return null;
                    const grantorIdsForTrustees = getFormValue(
                      "trust_info.grantorIds",
                      getTrustGrantorIds(selectedAccount.trust_info),
                    );
                    const grantorIdSet = new Set(
                      (Array.isArray(grantorIdsForTrustees) ? grantorIdsForTrustees : []).map(String),
                    );
                    const memberIds = members.map((m) => m.id).filter(Boolean);
                    const memberLabels = members.map(
                      (m) =>
                        `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim() || "Member",
                    );
                    const assignedAll = new Set();
                    for (let j = 0; j < rowCount; j++) {
                      const oid = getFormValue(
                        `trust_info.trustees.${j}.id`,
                        trusteesBase[j] ? getTrusteeMemberId(trusteesBase[j]) : "",
                      );
                      if (oid) assignedAll.add(String(oid));
                    }
                    const canAddMoreTrustees = memberIds.some(
                      (id) => !assignedAll.has(String(id)),
                    );
                    return (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Trustees</h4>
                        {Array.from({ length: rowCount }, (_, idx) => {
                          const baseRow = trusteesBase[idx];
                          const trusteeMemberId = getFormValue(
                            `trust_info.trustees.${idx}.id`,
                            baseRow ? getTrusteeMemberId(baseRow) : "",
                          );
                          const showRel =
                            !trusteeMemberId ||
                            !grantorIdSet.has(String(trusteeMemberId));
                          const taken = new Set();
                          for (let j = 0; j < rowCount; j++) {
                            if (j === idx) continue;
                            const oid = getFormValue(
                              `trust_info.trustees.${j}.id`,
                              trusteesBase[j] ? getTrusteeMemberId(trusteesBase[j]) : "",
                            );
                            if (oid) taken.add(String(oid));
                          }
                          const allowedIdx = memberIds
                            .map((id, mi) => ({ id, mi }))
                            .filter(
                              ({ id }) =>
                                !taken.has(String(id)) ||
                                String(id) === String(trusteeMemberId || ""),
                            );
                          const rowOptions = allowedIdx.map(({ id }) => id);
                          const rowLabels = allowedIdx.map(({ mi }) => memberLabels[mi] || "Member");
                          return (
                            <div
                              key={`${selectedAccountId}-trustee-${idx}-${trusteeMemberId || "empty"}`}
                              className={`grid grid-cols-1 gap-x-8 gap-y-1 mb-3 last:mb-0 ${showRel ? "md:grid-cols-2" : ""}`}
                            >
                              {isEditing ? (
                                <DataField
                                  label={`Trustee ${idx + 1}`}
                                  value={trusteeMemberId || ""}
                                  isEditable
                                  isEditing
                                  options={rowOptions}
                                  optionLabels={rowLabels}
                                  fieldPath={`trust_info.trustees.${idx}.id`}
                                  onFieldChange={handleFieldChange}
                                  getFormValue={getFormValue}
                                />
                              ) : (
                                <div>
                                  <span className="text-xs font-medium text-gray-500 block mb-0.5">
                                    Trustee {idx + 1}
                                  </span>
                                  <p className="text-sm text-gray-900 mt-0.5">
                                    {getMemberNameById(trusteeMemberId)}
                                  </p>
                                </div>
                              )}
                              {showRel && (
                                <DataField
                                  label="Relationship to Grantor"
                                  value={baseRow?.relationship_to_grantor || "N/A"}
                                  isEditable
                                  isEditing={isEditing}
                                  options={RELATIONSHIP_OPTIONS}
                                  fieldPath={`trust_info.trustees.${idx}.relationship_to_grantor`}
                                  onFieldChange={handleFieldChange}
                                  getFormValue={getFormValue}
                                />
                              )}
                            </div>
                          );
                        })}
                        {isEditing && (
                          <button
                            type="button"
                            disabled={!canAddMoreTrustees}
                            title={
                              canAddMoreTrustees
                                ? ""
                                : "Every household member is already assigned as a trustee."
                            }
                            onClick={() => {
                              handleFieldChange(`trust_info.trustees.${rowCount}.id`, "");
                              handleFieldChange(
                                `trust_info.trustees.${rowCount}.relationship_to_grantor`,
                                "",
                              );
                            }}
                            className="mt-2 px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            + Add trustee
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}

            {/* ═══════════════ BUSINESS INFO (LLC) ═══════════════ */}
            {selectedSection === "business_info" &&
              selectedAccount.registration?.toLowerCase() === "business" && (
                <>
                  {(() => {
                    const biz = selectedAccount.business_info || {};
                    const bizAddr = biz.address || {};
                    const taxOptions = getTaxClassificationOptions(
                      getFormValue("type", selectedAccount.type),
                    );

                    return (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                          <DataField
                            label="Business Name"
                            value={biz.business_name || "N/A"}
                            isEditable
                            isEditing={isEditing}
                            fieldPath="business_info.business_name"
                            onFieldChange={handleFieldChange}
                            getFormValue={getFormValue}
                          />
                          {taxOptions.length > 0 && (
                            <DataField
                              label="Tax Classification"
                              value={biz.tax_classification || "N/A"}
                              isEditable
                              isEditing={isEditing}
                              options={taxOptions}
                              fieldPath="business_info.tax_classification"
                              onFieldChange={handleFieldChange}
                              getFormValue={getFormValue}
                            />
                          )}
                          <DataField
                            label="Is Operating"
                            value={
                              isEditing
                                ? biz.is_operating === true
                                  ? "true"
                                  : biz.is_operating === false
                                    ? "false"
                                    : ""
                                : biz.is_operating === true
                                  ? "Yes"
                                  : biz.is_operating === false
                                    ? "No"
                                    : "N/A"
                            }
                            isEditable
                            isEditing={isEditing}
                            options={["true", "false"]}
                            optionLabels={["Yes", "No"]}
                            fieldPath="business_info.is_operating"
                            onFieldChange={handleFieldChange}
                            getFormValue={getFormValue}
                          />
                          <ToggleField
                            label="Is this a non-profit organization?"
                            value={biz.is_non_profit ?? false}
                            isEditable
                            isEditing={isEditing}
                            fieldPath="business_info.is_non_profit"
                            onFieldChange={handleFieldChange}
                            formData={formData?.[selectedAccountId]}
                            variant="field"
                          />
                          <DataField
                            label="Address Line 1"
                            value={bizAddr.address_line_1 || "N/A"}
                            isEditable
                            isEditing={isEditing}
                            fieldPath="business_info.address.address_line_1"
                            onFieldChange={handleFieldChange}
                            getFormValue={getFormValue}
                          />
                          <DataField
                            label="Address Line 2"
                            value={bizAddr.address_line_2 || "N/A"}
                            isEditable
                            isEditing={isEditing}
                            fieldPath="business_info.address.address_line_2"
                            onFieldChange={handleFieldChange}
                            getFormValue={getFormValue}
                          />
                        </div>
                        {isEditing ? (
                          <div className="mt-2 md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                            <StateCitySelect
                              countryValue={getFormValue(
                                "business_info.address.country",
                                bizAddr.country || "USA",
                              )}
                              stateValue={getFormValue(
                                "business_info.address.province",
                                bizAddr.province || "",
                              )}
                              cityValue={getFormValue(
                                "business_info.address.city",
                                bizAddr.city || "",
                              )}
                              onCountryChange={(val) => {
                                handleFieldChange("business_info.address.country", val);
                                handleFieldChange("business_info.address.province", "");
                                handleFieldChange("business_info.address.city", "");
                              }}
                              onStateChange={(val) => {
                                handleFieldChange("business_info.address.province", val);
                                handleFieldChange(
                                  "business_info.address.country",
                                  getFormValue("business_info.address.country", bizAddr.country) || "USA",
                                );
                              }}
                              onCityChange={(val) => {
                                handleFieldChange("business_info.address.city", val);
                                handleFieldChange(
                                  "business_info.address.country",
                                  getFormValue("business_info.address.country", bizAddr.country) || "USA",
                                );
                              }}
                              isEditing={true}
                              stateLabel="Province/State"
                              cityLabel="City"
                              zipValue={getFormValue(
                                "business_info.address.postal_code",
                                bizAddr.postal_code || "",
                              )}
                              onZipChange={(val) =>
                                handleFieldChange("business_info.address.postal_code", val)
                              }
                              zipLabel="Postal Code"
                              zipRestrictUsFive
                              zipError={
                                validationErrors?.[`${selectedAccountId}.business_info.address.postal_code`]
                              }
                              onZipValidationError={(msg) =>
                                accountScopedFieldError("business_info.address.postal_code", msg)
                              }
                            />
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                            <DataField label="Country" value={bizAddr.country || "N/A"} />
                            <DataField label="Province/State" value={bizAddr.province || "N/A"} />
                            <DataField label="City" value={bizAddr.city || "N/A"} />
                            <DataField label="Postal Code" value={bizAddr.postal_code || "N/A"} />
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 mt-2">
                          <DataField
                            label="Phone"
                            value={bizAddr.phone || "N/A"}
                            isEditable
                            isEditing={isEditing}
                            type="phone"
                            fieldPath="business_info.address.phone"
                            onFieldChange={handleFieldChange}
                            getFormValue={getFormValue}
                            error={validationErrors?.[`${selectedAccountId}.business_info.address.phone`]}
                            onFieldError={accountScopedFieldError}
                          />
                          <DataField
                            label="Business Tax ID (EIN)"
                            value={biz.ssn || "N/A"}
                            isEditable
                            isEditing={isEditing}
                            type="ssn"
                            ssnLengthOnly
                            fieldPath="business_info.ssn"
                            onFieldChange={handleFieldChange}
                            getFormValue={getFormValue}
                            error={validationErrors?.[`${selectedAccountId}.business_info.ssn`]}
                            onFieldError={accountScopedFieldError}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4 items-start">
                          <MemberSelect
                            multi
                            label="Control Persons"
                            members={members}
                            isEditing={isEditing}
                            value={getFormValue(
                              "business_info.control_persons",
                              Array.isArray(biz.control_persons) ? biz.control_persons : [],
                            )}
                            onChange={(next) => handleFieldChange("business_info.control_persons", next)}
                            addLabel="+ Add Control Person"
                          />
                          <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Notes
                            </label>
                            {isEditing && (
                              <span
                                className={`text-[11px] tabular-nums ${
                                  (getFormValue("business_info.notes", biz.notes) || "").length >= 950
                                    ? "text-red-500 font-semibold"
                                    : "text-gray-400"
                                }`}
                              >
                                {(getFormValue("business_info.notes", biz.notes) || "").length}/1000
                              </span>
                            )}
                          </div>
                          {isEditing ? (
                            <textarea
                              value={getFormValue("business_info.notes", biz.notes) || ""}
                              onChange={(e) => handleFieldChange("business_info.notes", e.target.value)}
                              maxLength={1000}
                              rows={5}
                              placeholder="Add notes about this business account"
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 resize-y overflow-y-auto shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25 placeholder:text-gray-400 min-h-[100px] max-h-[280px]"
                            />
                          ) : (
                            <div className="min-h-[60px] max-h-[200px] overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap">
                              {biz.notes || <span className="text-gray-400 italic">—</span>}
                            </div>
                          )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}

            {/* ═══════════════ INHERITANCE INFO ═══════════════ */}
            {selectedSection === "inheritance_info" &&
              INHERITED_ACCOUNT_TYPES.includes(selectedAccount.type) && (
                <>
                  {(() => {
                    const dec = selectedAccount.inheritance_info?.decedent_info || {};
                    const decId = getFormValue(
                      "inheritance_info.decedent_info.id",
                      dec.id || "",
                    );
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                        <MemberSelect
                          label="Decedent"
                          members={members}
                          isEditing={isEditing}
                          value={decId}
                          onChange={(id) =>
                            handleFieldChange("inheritance_info.decedent_info.id", id)
                          }
                        />
                        <DataField
                          getFormValue={getFormValue}
                          label="Owner's Relationship to Decedent"
                          value={dec.relationship}
                          isEditable={true}
                          isEditing={isEditing}
                          options={RELATIONSHIP_OPTIONS}
                          fieldPath="inheritance_info.decedent_info.relationship"
                          onFieldChange={handleFieldChange}
                        />
                      </div>
                    );
                  })()}
                </>
              )}

            {/* ═══════════════ ESTATE INFO ═══════════════ */}
            {selectedSection === "estate_info" &&
              ESTATE_ACCOUNT_TYPES.includes(selectedAccount.type) && (
                <>
                  {(() => {
                    const formExecutors = getFormValue(
                      "estate_info.executors",
                      selectedAccount.estate_info?.executors,
                    );
                    const baseRows = Array.isArray(formExecutors) ? formExecutors : [];
                    const rows = baseRows.length > 0 ? baseRows : [{ id: "" }];
                    const setExec = (next) => handleFieldChange("estate_info.executors", next);
                    const updateAt = (idx, value) =>
                      setExec(rows.map((r, i) => (i === idx ? { ...r, id: value } : r)));
                    const removeAt = (idx) => setExec(rows.filter((_, i) => i !== idx));
                    const addRow = () => setExec([...rows, { id: "" }]);
                    const assignedIds = new Set(rows.map((r) => String(r?.id || "")).filter(Boolean));
                    const canAdd =
                      rows.length < 2 &&
                      members.filter((m) => !assignedIds.has(String(m.id))).length > 0;
                    return (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Executors (max 2)
                        </label>
                        {!isEditing ? (
                          <div className="text-gray-900">
                            {rows.filter((r) => r?.id).length === 0 ? (
                              <div className="text-gray-400">—</div>
                            ) : (
                              rows.filter((r) => r?.id).map((r, idx) => (
                                <div key={`exec-ro-${idx}`} className="text-sm">
                                  {getMemberNameById(r.id) || "—"}
                                </div>
                              ))
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {rows.map((row, idx) => (
                              <div key={`exec-${idx}`} className="flex gap-2 items-end">
                                <select
                                  value={row.id || ""}
                                  onChange={(e) => updateAt(idx, e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                  <option value="">Select member...</option>
                                  {members
                                    .filter(
                                      (m) =>
                                        !rows.some(
                                          (r, i) => i !== idx && String(r?.id || "") === String(m.id),
                                        ),
                                    )
                                    .map((m) => (
                                      <option key={m.id} value={m.id}>
                                        {`${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim()}
                                      </option>
                                    ))}
                                </select>
                                {rows.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeAt(idx)}
                                    className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                            {canAdd && (
                              <button
                                type="button"
                                onClick={addRow}
                                className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm"
                              >
                                + Add Executor
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}

            {/* ═══════════════ OTHER INFO ═══════════════ */}
            {selectedSection === "other_info" &&
              isSpecialRegistration(selectedAccount.registration) && (
                <>
                  {(() => {
                    const otherInfo = selectedAccount.other_info || {};
                    const otherOwners = Array.isArray(otherInfo.owners) ? otherInfo.owners : [];
                    const memberNameOptions =
                      members
                        .map((m) => `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim())
                        .filter(Boolean);

                    const formOwners = getFormValue("other_info.owners", null);
                    const displayOwners =
                      formOwners && typeof formOwners === "object"
                        ? (() => {
                            const formKeys = Object.keys(formOwners).map(Number).filter((n) => !isNaN(n));
                            const len = formOwners._length !== undefined
                              ? formOwners._length
                              : Math.max(otherOwners.length - 1, ...formKeys) + 1;
                            return Array.from({ length: len }, (_, i) => ({
                              full_name:
                                formOwners[i]?.full_name !== undefined
                                  ? formOwners[i].full_name
                                  : otherOwners[i]?.full_name || "",
                            }));
                          })()
                        : otherOwners;

                    return (
                      <>
                        {/* Owners */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Owners</h4>
                          <div className="space-y-2">
                            {displayOwners.map((owner, idx) => (
                              <div key={idx} className="flex items-end gap-2">
                                <div className="flex-1">
                                  <DataField
                                    label={`Owner ${idx + 1}`}
                                    value={owner.full_name || "N/A"}
                                    isEditable
                                    isEditing={isEditing}
                                    options={memberNameOptions}
                                    allowCustom
                                    fieldPath={`other_info.owners.${idx}.full_name`}
                                    onFieldChange={handleFieldChange}
                                    getFormValue={getFormValue}
                                  />
                                </div>
                                {isEditing && displayOwners.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const remaining = displayOwners.filter((_, i) => i !== idx);
                                      const rebuilt = {};
                                      remaining.forEach((o, i) => {
                                        rebuilt[i] = { full_name: o.full_name };
                                      });
                                      rebuilt._length = remaining.length;
                                      handleFieldChange("other_info.owners", rebuilt);
                                    }}
                                    className="mb-3 p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded hover:bg-red-50"
                                    title="Remove owner"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ))}
                            {isEditing && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newIdx = displayOwners.length;
                                  handleFieldChange(`other_info.owners.${newIdx}.full_name`, "");
                                  handleFieldChange("other_info.owners._length", newIdx + 1);
                                }}
                                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium mt-1"
                              >
                                + Add Owner
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Note */}
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Note
                            </label>
                            {isEditing && (
                              <span
                                className={`text-[11px] tabular-nums ${
                                  (getFormValue("other_info.note", otherInfo.note) || "").length >=
                                  950
                                    ? "text-red-500 font-semibold"
                                    : "text-gray-400"
                                }`}
                              >
                                {(getFormValue("other_info.note", otherInfo.note) || "").length}/1000
                              </span>
                            )}
                          </div>
                          {isEditing ? (
                            <textarea
                              value={getFormValue("other_info.note", otherInfo.note) || ""}
                              onChange={(e) => handleFieldChange("other_info.note", e.target.value)}
                              maxLength={1000}
                              rows={5}
                              placeholder="Add notes about this account"
                              className="w-3/5 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 resize-y overflow-y-auto shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25 placeholder:text-gray-400 min-h-[100px] max-h-[280px]"
                            />
                          ) : (
                            <div className="min-h-[60px] max-h-[200px] overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap">
                              {otherInfo.note || <span className="text-gray-400 italic">—</span>}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}

            {/* ═══════════════ BENEFICIARIES ═══════════════ */}
            {selectedSection === "beneficiaries" && (
              <>
                {(() => {
                  const removedIds = removedBeneficiaries?.[selectedAccount.id] || [];
                  const visibleBeneficiaries = (selectedAccount.beneficiaries || [])
                    .map((b, idx) => ({ ben: b, originalIdx: idx }))
                    .filter(({ ben }) => !removedIds.includes(ben?.id));

                  const accountOwnerIds = (selectedAccount.owners_info || []).map((o) => o.id);

                  const primaryTotal = visibleBeneficiaries.reduce((sum, { ben, originalIdx }) => {
                    const formType = getFormValue(`beneficiaries.${originalIdx}.type`, null);
                    const type = formType ?? ben.type;
                    if (type !== "primary") return sum;
                    const formShare = getFormValue(`beneficiaries.${originalIdx}.share`, null);
                    const share = parseFloat(formShare ?? ben.share) || 0;
                    return sum + share;
                  }, 0);

                  const contingentTotal = visibleBeneficiaries.reduce((sum, { ben, originalIdx }) => {
                    const formType = getFormValue(`beneficiaries.${originalIdx}.type`, null);
                    const type = formType ?? ben.type;
                    if (type !== "contingent") return sum;
                    const formShare = getFormValue(`beneficiaries.${originalIdx}.share`, null);
                    const share = parseFloat(formShare ?? ben.share) || 0;
                    return sum + share;
                  }, 0);

                  const hasPrimary = visibleBeneficiaries.some(({ ben, originalIdx }) => {
                    const formType = getFormValue(`beneficiaries.${originalIdx}.type`, null);
                    return (formType ?? ben.type) === "primary";
                  });
                  const hasContingent = visibleBeneficiaries.some(({ ben, originalIdx }) => {
                    const formType = getFormValue(`beneficiaries.${originalIdx}.type`, null);
                    return (formType ?? ben.type) === "contingent";
                  });

                  return (
                    <>
                      {visibleBeneficiaries.length > 0 && (isEditing || hasPrimary || hasContingent) && (
                        <div className="flex items-center gap-3 mb-3 text-sm font-medium">
                          {hasPrimary && (
                            <span
                              className={
                                isBeneficiaryShareTotalOver100(primaryTotal)
                                  ? "text-red-500"
                                  : isBeneficiaryShareTotalComplete(primaryTotal)
                                    ? "text-emerald-600"
                                    : "text-amber-600"
                              }
                            >
                              Primary: {primaryTotal}%
                            </span>
                          )}
                          {hasPrimary && hasContingent && <span className="text-gray-400">|</span>}
                          {hasContingent && (
                            <span
                              className={
                                isBeneficiaryShareTotalOver100(contingentTotal)
                                  ? "text-red-500"
                                  : isBeneficiaryShareTotalComplete(contingentTotal)
                                    ? "text-emerald-600"
                                    : "text-amber-600"
                              }
                            >
                              Contingent: {contingentTotal}%
                            </span>
                          )}
                        </div>
                      )}
                      {visibleBeneficiaries.length > 0 ? (
                        <div className="space-y-3">
                          {visibleBeneficiaries.map(({ ben, originalIdx }) => {
                            const memberIdValue = getFormValue(`beneficiaries.${originalIdx}.household_member_id`, ben.household_member_id);
                            const typeValue = getFormValue(`beneficiaries.${originalIdx}.type`, ben.type);
                            const shareValue = parseFloat(getFormValue(`beneficiaries.${originalIdx}.share`, ben.share)) || 0;
                            const relationshipValue = getFormValue(`beneficiaries.${originalIdx}.relationship`, ben.relationship || "");

                            return (
                              <div key={ben.id || originalIdx} className="border border-gray-200 rounded-lg p-4 space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-500 mb-1">Member</label>
                                  {isEditing ? (
                                    <select
                                      value={memberIdValue || ""}
                                      onChange={(e) => handleFieldChange(`beneficiaries.${originalIdx}.household_member_id`, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    >
                                      <option value="">Select member</option>
                                      {members.map((m) => {
                                        const isOwner = accountOwnerIds.includes(m.id);
                                        return (
                                          <option key={m.id} value={m.id} disabled={isOwner}>
                                            {getMemberNameById(m.id)}{isOwner ? " (Owner)" : ""}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  ) : (
                                    <p className="text-sm text-gray-900">{getMemberNameById(ben.household_member_id)}</p>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-500 mb-1">Type</label>
                                  {isEditing ? (
                                    <select
                                      value={typeValue || ""}
                                      onChange={(e) => handleFieldChange(`beneficiaries.${originalIdx}.type`, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    >
                                      <option value="">Select type</option>
                                      <option value="primary">Primary</option>
                                      <option value="contingent">Contingent</option>
                                    </select>
                                  ) : (
                                    <p className="text-sm text-gray-900">
                                      {ben.type === "primary" ? "Primary" : ben.type === "contingent" ? "Contingent" : ben.type || "N/A"}
                                    </p>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-500 mb-1">Relationship to Account Owner</label>
                                  {isEditing ? (
                                    <select
                                      value={relationshipValue || ""}
                                      onChange={(e) => handleFieldChange(`beneficiaries.${originalIdx}.relationship`, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    >
                                      <option value="">Select relationship to account owner</option>
                                      {RELATIONSHIP_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>
                                          {opt}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <p className="text-sm text-gray-900">{ben.relationship || "N/A"}</p>
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-4 mb-1">
                                    <label className="text-sm font-medium text-gray-500">Share %</label>
                                    {isEditing && (
                                      <SharePresetButtons
                                        value={shareValue}
                                        onChange={(val) => handleFieldChange(`beneficiaries.${originalIdx}.share`, val)}
                                      />
                                    )}
                                  </div>
                                  {isEditing ? (
                                    <ShareSlider
                                      value={shareValue}
                                      onChange={(val) => handleFieldChange(`beneficiaries.${originalIdx}.share`, val)}
                                    />
                                  ) : (
                                    <p className="text-sm text-gray-900">{ben.share != null ? `${ben.share}%` : "N/A"}</p>
                                  )}
                                </div>
                                {isEditing && onRemoveBeneficiary && (
                                  <button
                                    type="button"
                                    onClick={() => onRemoveBeneficiary(selectedAccount.id, ben.id)}
                                    className="w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No beneficiaries listed for this account.</p>
                      )}
                    </>
                  );
                })()}
                {isEditing && onAddBeneficiary && (
                  <button
                    type="button"
                    onClick={() => onAddBeneficiary(selectedAccount.id)}
                    className="mt-3 w-full py-3 border-2 border-dashed border-emerald-200 text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50 rounded-xl transition-all flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Beneficiary
                  </button>
                )}
              </>
            )}

            {/* ═══════════════ TRUSTED CONTACTS ═══════════════ */}
            {selectedSection === "trusted_contacts" && (
              <>
                {(() => {
                  const removedIds = removedTrustedContacts?.[selectedAccount.id] || [];
                  const visibleContacts = (selectedAccount.trusted_contacts || [])
                    .map((tc, idx) => ({ tc, originalIdx: idx }))
                    .filter(({ tc }) => !removedIds.includes(tc?.id));

                  return visibleContacts.length > 0 ? (
                    <div className="space-y-3">
                      {visibleContacts.map(({ tc, originalIdx }) => {
                        const memberIdValue = getFormValue(`trusted_contacts.${originalIdx}.household_member_id`, tc.household_member_id);
                        return (
                          <div key={tc.id || originalIdx} className="border border-gray-200 rounded-lg p-4 space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-500 mb-1">Member</label>
                              {isEditing ? (
                                <select
                                  value={memberIdValue || ""}
                                  onChange={(e) => handleFieldChange(`trusted_contacts.${originalIdx}.household_member_id`, e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                >
                                  <option value="">Select member</option>
                                  {members.map((m) => (
                                    <option key={m.id} value={m.id}>
                                      {getMemberNameById(m.id)}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-sm text-gray-900">{getMemberNameById(tc.household_member_id)}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500 mb-1">Relationship to Account Owner</label>
                              {isEditing ? (
                                <select
                                  value={getFormValue(`trusted_contacts.${originalIdx}.relationship`, tc.relationship || "")}
                                  onChange={(e) => handleFieldChange(`trusted_contacts.${originalIdx}.relationship`, e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                >
                                  <option value="">Select relationship to account owner</option>
                                  {RELATIONSHIP_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-sm text-gray-900">{tc.relationship || "N/A"}</p>
                              )}
                            </div>
                            {isEditing && onRemoveTrustedContact && (
                              <button
                                type="button"
                                onClick={() => onRemoveTrustedContact(selectedAccount.id, tc.id)}
                                className="w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No trusted contacts listed for this account.</p>
                  );
                })()}
                {isEditing && onAddTrustedContact && (
                  <button
                    type="button"
                    onClick={() => onAddTrustedContact(selectedAccount.id)}
                    className="mt-3 w-full py-3 border-2 border-dashed border-indigo-200 text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50 rounded-xl transition-all flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Trusted Contact
                  </button>
                )}
              </>
            )}

            {/* ═══════════════ BANK ACCOUNT DETAILS ═══════════════ */}
            {selectedSection === "bank_account_details" && (
              <>
                {(() => {
                  const ops = formData?.[selectedAccountId]?.bankAccountsOps || emptyBankOps();
                  const visibleBanks = resolveVisibleBanks(
                    selectedAccount,
                    householdData?.bank_accounts || [],
                    ops,
                  );
                  const pendingIds = {
                    added: ops.addExisting || [],
                    removed: ops.remove || [],
                  };

                  return (
                    <div className="space-y-4">
                      {isEditing && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-start gap-2">
                          <svg className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p>Bank account details can be edited in the Bank Accounts tab. You can link or unlink bank accounts here.</p>
                        </div>
                      )}

                      <BankAccountAccordion
                        banks={visibleBanks}
                        pendingIds={pendingIds}
                        isEditing={isEditing}
                        fieldsReadOnly
                        onUnlink={(baId) => onRemoveBankAccount && onRemoveBankAccount(selectedAccount.id, baId)}
                        onAdd={onLinkBankAccount ? () => onLinkBankAccount(selectedAccount.id) : null}
                      />
                    </div>
                  );
                })()}
              </>
            )}

            {/* ═══════════════ CONTRIBUTIONS ═══════════════ */}
            {selectedSection === "contributions" && (
              <>
                {selectedAccount.deposits && selectedAccount.deposits.length > 0 ? (
                  <div className="space-y-3">
                    {selectedAccount.deposits.map((dep, idx) => (
                      <div key={dep.id || idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                            <div>
                              <span className="text-xs font-medium text-gray-500">Amount</span>
                              {isEditing ? (
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={getFormValue(`deposits.${idx}.amount`, dep.amount) ?? ""}
                                  onChange={(e) => handleFieldChange(`deposits.${idx}.amount`, e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0.00"
                                />
                              ) : (
                                <p className="text-sm text-gray-900">{formatCurrencyPreservingScale(dep.amount)}</p>
                              )}
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500">Frequency</span>
                              {isEditing ? (
                                <select
                                  value={normalizeAccountOptionValue(getFormValue(`deposits.${idx}.frequency`, dep.frequency)) || ""}
                                  onChange={(e) => handleFieldChange(`deposits.${idx}.frequency`, e.target.value)}
                                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                >
                                  <option value="">Select frequency</option>
                                  {FREQUENCY_OPTIONS.map((o) => (
                                    <option key={o} value={o}>{o}</option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-sm text-gray-900">{dep.frequency || "N/A"}</p>
                              )}
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500">Start Date</span>
                              {isEditing ? (
                                <>
                                  <input
                                    type="date"
                                    min={scheduleBounds.min}
                                    max={scheduleBounds.max}
                                    value={formatDateForInput(getFormValue(`deposits.${idx}.start_date`, dep.start_date)) || ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      handleFieldChange(`deposits.${idx}.start_date`, v);
                                      const errKey = `deposits.${idx}.start_date`;
                                      const msg = v
                                        ? validateScheduleFutureYmd(normalizeToYmd(v), "Start date")
                                        : null;
                                      accountScopedFieldError(errKey, msg);
                                    }}
                                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                  />
                                  {validationErrors?.[`${selectedAccountId}.deposits.${idx}.start_date`] && (
                                    <p className="text-red-500 text-xs mt-1">
                                      {validationErrors[`${selectedAccountId}.deposits.${idx}.start_date`]}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-gray-900">{formatDateForDisplay(dep.start_date)}</p>
                              )}
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500">Method</span>
                              {isEditing ? (
                                <select
                                  value={normalizeAccountOptionValue(getFormValue(`deposits.${idx}.method`, dep.method)) || ""}
                                  onChange={(e) => handleFieldChange(`deposits.${idx}.method`, e.target.value)}
                                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                >
                                  <option value="">Select method</option>
                                  {DEPOSIT_METHOD_OPTIONS.map((o) => (
                                    <option key={o} value={o}>{o}</option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-sm text-gray-900">{dep.method || "N/A"}</p>
                              )}
                            </div>
                          </div>
                          {isEditing && dep.id && onDeleteDeposit && (
                            <button
                              type="button"
                              onClick={() => onDeleteDeposit(selectedAccount.id, dep.id)}
                              className="ml-2 p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No contributions recorded for this account.</p>
                )}
                {isEditing && onAddContribution && (
                  <button
                    type="button"
                    onClick={() => onAddContribution(selectedAccount)}
                    className="mt-3 w-full py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 font-semibold shadow-md shadow-emerald-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Contribution
                  </button>
                )}
              </>
            )}

            {/* ═══════════════ WITHDRAWALS ═══════════════ */}
            {selectedSection === "withdrawals" && (
              <>
                {selectedAccount.withdrawals && selectedAccount.withdrawals.length > 0 ? (
                  <div className="space-y-3">
                    {selectedAccount.withdrawals.map((wd, idx) => {
                      const currentDestType = getFormValue(`withdrawals.${idx}.destination_type`, wd.destination_type) || "";
                      const currentDestId = getFormValue(`withdrawals.${idx}.destination_identifier`, wd.destination_identifier) || "";

                      const brokerageOpts = accounts
                        .filter((a) => a.id !== selectedAccount.id)
                        .map((acc) => {
                          const idx = accounts.findIndex(
                            (a) => String(a.id) === String(acc.id),
                          );
                          return {
                            value: acc.id,
                            label: formatAccountName(
                              acc,
                              idx >= 0 ? idx + 1 : 1,
                              members,
                            ),
                          };
                        });

                      const bankOpts = [];
                      accounts.forEach((a) => {
                        getAccountBanks(a).forEach((ba) => {
                          if (ba?.id && !bankOpts.find((o) => o.value === ba.id)) {
                            bankOpts.push({ value: ba.id, label: formatBankLabel(ba) });
                          }
                        });
                      });
                      (householdData?.bank_accounts || []).forEach((ba) => {
                        if (!bankOpts.find((o) => o.value === ba.id)) {
                          bankOpts.push({ value: ba.id, label: formatBankLabel(ba) });
                        }
                      });

                      const destOptions = currentDestType === "Brokerage Account" ? brokerageOpts : currentDestType === "Bank Account" ? bankOpts : [];
                      const destLabel = currentDestType === "Brokerage Account" ? "Brokerage Account" : currentDestType === "Bank Account" ? "Bank Account" : "Destination";

                      const resolveDestName = () => {
                        if (!wd.destination_identifier) return "N/A";
                        const opt = [...brokerageOpts, ...bankOpts].find((o) => o.value === wd.destination_identifier);
                        return opt ? opt.label : wd.destination_identifier;
                      };

                      return (
                        <div key={wd.id || idx} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                              <div>
                                <span className="text-xs font-medium text-gray-500">Amount</span>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={getFormValue(`withdrawals.${idx}.amount`, wd.amount) ?? ""}
                                    onChange={(e) => handleFieldChange(`withdrawals.${idx}.amount`, e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    placeholder="0.00"
                                  />
                                ) : (
                                  <p className="text-sm text-gray-900">{formatCurrencyPreservingScale(wd.amount)}</p>
                                )}
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500">Frequency</span>
                                {isEditing ? (
                                  <select
                                    value={normalizeAccountOptionValue(getFormValue(`withdrawals.${idx}.frequency`, wd.frequency)) || ""}
                                    onChange={(e) => handleFieldChange(`withdrawals.${idx}.frequency`, e.target.value)}
                                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                                  >
                                    <option value="">Select frequency</option>
                                    {FREQUENCY_OPTIONS.map((o) => (
                                      <option key={o} value={o}>{o}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <p className="text-sm text-gray-900">{wd.frequency || "N/A"}</p>
                                )}
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500">Start Date</span>
                                {isEditing ? (
                                  <>
                                    <input
                                      type="date"
                                      min={scheduleBounds.min}
                                      max={scheduleBounds.max}
                                      value={formatDateForInput(getFormValue(`withdrawals.${idx}.start_date`, wd.start_date)) || ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        handleFieldChange(`withdrawals.${idx}.start_date`, v);
                                        const errKey = `withdrawals.${idx}.start_date`;
                                        const msg = v
                                          ? validateScheduleFutureYmd(normalizeToYmd(v), "Start date")
                                          : null;
                                        accountScopedFieldError(errKey, msg);
                                      }}
                                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                                    />
                                    {validationErrors?.[`${selectedAccountId}.withdrawals.${idx}.start_date`] && (
                                      <p className="text-red-500 text-xs mt-1">
                                        {validationErrors[`${selectedAccountId}.withdrawals.${idx}.start_date`]}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-900">{formatDateForDisplay(wd.start_date)}</p>
                                )}
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500">Destination Type</span>
                                {isEditing ? (
                                  <select
                                    value={currentDestType}
                                    onChange={(e) => {
                                      handleFieldChange(`withdrawals.${idx}.destination_type`, e.target.value);
                                      handleFieldChange(`withdrawals.${idx}.destination_identifier`, "");
                                    }}
                                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                                  >
                                    <option value="">Select destination</option>
                                    {DESTINATION_TYPE_OPTIONS.map((o) => (
                                      <option key={o} value={o}>{o}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <p className="text-sm text-gray-900">{wd.destination_type || "N/A"}</p>
                                )}
                              </div>
                              {(currentDestType || wd.destination_identifier) && (
                                <div className="md:col-span-2">
                                  <span className="text-xs font-medium text-gray-500">{destLabel}</span>
                                  {isEditing && currentDestType ? (
                                    <select
                                      value={currentDestId}
                                      onChange={(e) => handleFieldChange(`withdrawals.${idx}.destination_identifier`, e.target.value)}
                                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                                    >
                                      <option value="">Select {destLabel.toLowerCase()}</option>
                                      {destOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <p className="text-sm text-gray-900">{resolveDestName()}</p>
                                  )}
                                </div>
                              )}
                            </div>
                            {isEditing && wd.id && onDeleteWithdrawal && (
                              <button
                                type="button"
                                onClick={() => onDeleteWithdrawal(selectedAccount.id, wd.id)}
                                className="ml-2 p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No withdrawals recorded for this account.</p>
                )}
                {isEditing && onAddWithdrawal && (
                  <button
                    type="button"
                    onClick={() => onAddWithdrawal(selectedAccount)}
                    className="mt-3 w-full py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all flex items-center justify-center gap-2 font-semibold shadow-md shadow-amber-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Withdrawal
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewAccountsTab;
